// Piè di pagina del negozio: informazioni della bottega + riga fissa Rarifex
// (non rimovibile: chi vende, pagamento protetto, condizioni della piattaforma).
import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ArrowRight, MapPin, ShieldCheck } from 'lucide-react';
import { exitShop } from '../lib/shopSession';
import { useShop } from './ShopContext';
import { RarifexMark } from './ShopUi';
import { contactLines } from './ShopLocation';

const SOCIAL_LABELS = {
  website: 'Sito web', instagram: 'Instagram', facebook: 'Facebook', tiktok: 'TikTok',
  youtube: 'YouTube', pinterest: 'Pinterest', linkedin: 'LinkedIn',
};

export const socialHref = (v) => (v.startsWith('http') ? v : `https://${v}`);

export default function ShopFooter() {
  const shop = useShop();
  const navigate = useNavigate();
  const { crafter, name, navItems, paths, style, hasSocial, branding } = shop;
  const sellerType = crafter.crafter_type === 'professionista' ? 'Artigiano professionista' : 'Venditore privato (hobbista)';

  const leaveToRarifex = (to) => {
    exitShop();
    navigate(to);
  };

  return (
    <footer className="mt-16 bg-shop-primary-dark text-white/80" data-testid="shop-footer">
      <div className="max-w-6xl mx-auto px-4 py-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
        <div className="lg:col-span-2">
          <p className={`${style.heading} text-xl text-white`}>{name}</p>
          {crafter.storefront_motto && <p className="mt-2 text-sm italic max-w-sm">{crafter.storefront_motto}</p>}
          {branding.contact ? (
            <p className="mt-3 text-sm flex items-start gap-1.5" data-testid="shop-footer-address">
              <MapPin className="w-4 h-4 mt-0.5 shrink-0" />
              <span>
                {contactLines(branding.contact).street && <span className="block">{contactLines(branding.contact).street}</span>}
                <span className="block">{contactLines(branding.contact).place}</span>
                {branding.contact.visits !== 'none' && branding.contact.hours && <span className="block text-white/60 mt-1 whitespace-pre-line">{branding.contact.hours}</span>}
              </span>
            </p>
          ) : crafter.city && (
            <p className="mt-3 text-sm flex items-center gap-1.5">
              <MapPin className="w-4 h-4" /> {crafter.city}{crafter.region ? `, ${crafter.region}` : ''}
            </p>
          )}
          <p className="mt-3 text-xs text-white/60">{sellerType}{crafter.partita_iva ? ` · P.IVA ${crafter.partita_iva}` : ''}</p>
        </div>
        <div>
          <p className="text-sm font-semibold text-white mb-3">La bottega</p>
          <ul className="space-y-2 text-sm">
            <li><Link to={paths.home} className="hover:text-white">Home</Link></li>
            {navItems.map((n) => <li key={n.key}><Link to={n.to} className="hover:text-white">{n.label}</Link></li>)}
          </ul>
        </div>
        <div>
          <p className="text-sm font-semibold text-white mb-3">Acquisti</p>
          <ul className="space-y-2 text-sm">
            <li><Link to={paths.page('spedizioni-resi')} className="hover:text-white" data-testid="shop-footer-shipping">Spedizioni e resi</Link></li>
            <li><Link to="/pagamenti" className="hover:text-white">Pagamenti</Link></li>
          </ul>
          {hasSocial && (
            <>
              <p className="text-sm font-semibold text-white mt-6 mb-3">Seguici</p>
              <ul className="flex flex-wrap gap-x-4 gap-y-2 text-sm">
                {Object.entries(crafter.social_links).filter(([, v]) => v).map(([k, v]) => (
                  <li key={k}><a href={socialHref(v)} target="_blank" rel="noopener noreferrer" className="hover:text-white">{SOCIAL_LABELS[k] || k}</a></li>
                ))}
              </ul>
            </>
          )}
        </div>
      </div>

      {/* Riga Rarifex fissa */}
      <div className="border-t border-white/15 bg-black/20" data-testid="shop-rarifex-row">
        <div className="max-w-6xl mx-auto px-4 py-4 flex flex-col md:flex-row md:items-center justify-between gap-3 text-xs text-white/70">
          <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
            <span className="inline-flex items-center gap-1.5 text-white">Bottega su <RarifexMark /></span>
            <span aria-hidden>·</span>
            <span className="inline-flex items-center gap-1"><ShieldCheck className="w-3.5 h-3.5" /> Pagamento protetto da Rarifex</span>
            <span aria-hidden>·</span>
            <Link to="/termini" className="hover:text-white">Termini</Link>
            <span aria-hidden>·</span>
            <Link to="/privacy" className="hover:text-white">Privacy</Link>
            <span aria-hidden>·</span>
            <Link to="/resi-rimborsi" className="hover:text-white">Recesso</Link>
          </div>
          <button type="button" onClick={() => leaveToRarifex('/crafters')} className="inline-flex items-center gap-1 text-white hover:underline" data-testid="shop-exit-rarifex">
            Scopri altri artigiani Made in Italy <ArrowRight className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>
    </footer>
  );
}
